import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, sendPasswordResetEmail, User } from 'firebase/auth';
import { Button, TextField, Typography, Container, Box, Paper, Alert, Link } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { auth } from '../firebase';
import { getUserProfile } from '../services/userService';
import { useTranslation } from 'react-i18next';

const Auth: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isLogin, setIsLogin] = useState<boolean>(true);
    const [isResetPassword, setIsResetPassword] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
            if (user) {
                const userProfile = await getUserProfile(user.uid);
                if (!userProfile || !userProfile.location) {
                    navigate('/onboarding');
                } else {
                    navigate('/tasks');
                }
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        try {
            if (isResetPassword) {
                if (!email) {
                    setError(t('auth.error_email_required'));
                    return;
                }
                await sendPasswordResetEmail(auth, email);
                setSuccessMessage(t('auth.success_reset'));
                // Optionally switch back to login after some time
            } else if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
            }
        } catch (error: any) {
            setError(error.message);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (error: any) {
            setError(error.message);
        }
    };

    const theme = useTheme();
    const loraTheme = React.useMemo(() => createTheme(theme, {
        typography: {
            fontFamily: "'Lora', serif",
            h3: { fontFamily: "'Lora', serif" },
            subtitle1: { fontFamily: "'Lora', serif" },
            body1: { fontFamily: "'Lora', serif" },
            body2: { fontFamily: "'Lora', serif" },
        },
        components: {
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontFamily: "'Inter', sans-serif"
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontFamily: "'Inter', sans-serif"
                    }
                }
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        fontFamily: "'Inter', sans-serif"
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontFamily: "'Inter', sans-serif"
                    }
                }
            }
        }
    }), [theme]);

    return (
        <ThemeProvider theme={loraTheme}>
        <Box 
            sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                background: 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)',
                p: 2
            }}
        >
            <Container component="main" maxWidth="sm">
                <Paper 
                    elevation={0} 
                    sx={{ 
                        padding: { xs: 4, md: 5 }, 
                        width: '100%', 
                        borderRadius: 4, 
                        border: '1px solid rgba(46,125,50,0.15)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                        bgcolor: 'white'
                    }}
                >
                    <Box 
                        onClick={() => navigate('/')} 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            mb: 3,
                            '&:hover': {
                                opacity: 0.85,
                                transform: 'scale(1.02)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        <Box 
                            component="img" 
                            src="/icons/generic-seedling.webp" 
                            alt="PocketGarden Logo" 
                            sx={{ width: 48, height: 48, objectFit: 'contain', mb: 1 }} 
                        />
                        <Typography 
                            component="h1" 
                            variant="h4" 
                            align="center" 
                            sx={{ fontWeight: 800, color: 'primary.dark', fontFamily: "'Lora', serif" }}
                        >
                            {t('app_name')}
                        </Typography>
                    </Box>
                    {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                    {successMessage && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{successMessage}</Alert>}

                    {!isResetPassword && (
                        <>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{
                                    mb: 2.5,
                                    height: 48,
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    bgcolor: '#4285F4',
                                    color: 'white',
                                    boxShadow: '0 4px 12px rgba(66, 133, 244, 0.2)',
                                    '&:hover': {
                                        bgcolor: '#357ae8',
                                        boxShadow: '0 6px 16px rgba(66, 133, 244, 0.3)',
                                    }
                                }}
                                onClick={handleGoogleSignIn}
                            >
                                {t('auth.sign_in_google')}
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                <Typography color="text.secondary" sx={{ px: 2, fontSize: '0.875rem' }}>
                                    {t('auth.or')}
                                </Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                            </Box>
                            <Typography component="h3" variant="subtitle1" align="center" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                {isLogin ? t('auth.login_with_email') : t('auth.signup_with_email')}
                            </Typography>
                        </>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label={t('auth.email_address')}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        {!isResetPassword && (
                            <>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label={t('auth.password')}
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                {isLogin && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Link
                                            component="button"
                                            type="button"
                                            variant="body2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault();
                                                setIsResetPassword(true);
                                                setError('');
                                                setSuccessMessage('');
                                            }}
                                            sx={{ textDecoration: 'none', fontWeight: 600 }}
                                        >
                                            {t('auth.forgot_password')}
                                        </Link>
                                    </Box>
                                )}
                            </>
                        )}

                        <Button 
                            type="submit" 
                            fullWidth 
                            variant="contained" 
                            sx={{ 
                                mt: 3, 
                                mb: 2, 
                                height: 50, 
                                fontWeight: 700, 
                                borderRadius: '50px',
                                textTransform: 'none',
                                fontSize: '1rem',
                                background: 'linear-gradient(45deg, #2E7D32, #4CAF50)',
                                boxShadow: '0 6px 20px rgba(46,125,50,0.2)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 8px 25px rgba(46,125,50,0.3)',
                                    background: 'linear-gradient(45deg, #1B5E20, #388E3C)'
                                }
                            }}
                        >
                            {isResetPassword ? t('auth.send_reset_link') : (isLogin ? t('auth.login_btn') : t('auth.sign_up'))}
                        </Button>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            {!isResetPassword ? (
                                <Typography variant="body2" color="text.secondary">
                                    {isLogin ? t('auth.dont_have_account') : t('auth.already_have_account')}{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={(e: React.MouseEvent) => {
                                            e.preventDefault();
                                            setIsLogin(!isLogin);
                                        }}
                                        sx={{ fontWeight: 800, cursor: 'pointer', textDecoration: 'none' }}
                                    >
                                        {isLogin ? t('auth.sign_up') : t('auth.sign_in')}
                                    </Link>
                                </Typography>
                            ) : (
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        setIsResetPassword(false);
                                        setError('');
                                        setSuccessMessage('');
                                    }}
                                    sx={{ textDecoration: 'none', fontWeight: 600 }}
                                >
                                    {t('auth.back_to_login')}
                                </Link>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
        </ThemeProvider>
    );
};

export default Auth;
